import { Button, Collapse } from "components/Core"
import { Select, Checkbox, Label, Spinner } from "flowbite-react"
import { useContext, useEffect, useState } from "react"
import { useLocation } from "react-router-dom";
import useSelectServiceOptions from "components/Filters/FieldType/FieldSelectService/useSelectServiceOptions"
import { LocationsContext } from "store/context/LocationsContext";

interface IFieldSelectService {
  field: any
  apply_filter: any
}

const FieldSelectService = ({ field: _field, apply_filter }: IFieldSelectService) => {

  const { id, label } = _field
  const { selectedRegion, selectedCommune, selectedCouriers, selectedPolygonType, selectedPolygon } = useContext(LocationsContext);
  const [value, set_value] = useState<string>("")
  const { options, isLoading, setSelectedRegion, setSelectedCommune, setSelectedCouriers, setSelectedPolygonType, setSelectedPolygon } = useSelectServiceOptions({ field: id })
  const { pathname } = useLocation();

  useEffect(() => {
    let contextValue = "";
    if (id === 'region') contextValue = selectedRegion || "";
    if (id === 'commune') contextValue = selectedCommune || "";
    // address couriers separate
    if (id === 'polygonType') contextValue = selectedPolygonType || "";
    if (id === 'polygon') contextValue = selectedPolygon?.id || "";
    set_value(contextValue);
  }, [id, selectedRegion, selectedCommune, selectedCouriers, selectedPolygonType, selectedPolygon]);

  const handle_change_value = (e: any) => {
    e.preventDefault()
    const selectedValue = e?.target?.value;
    set_value(selectedValue)
    if (id === 'region' && setSelectedRegion) {
      setSelectedRegion(selectedValue);
    }
    if (id === 'commune' && setSelectedCommune) {
      setSelectedCommune(selectedValue);
    }
    if (id === 'polygonType' && setSelectedPolygonType) {
      console.log("Polygon type changed:", selectedValue);
      setSelectedPolygonType(selectedValue);
      // Reset selectedPolygon to prevent mismatch (User action)
      if (setSelectedPolygon) setSelectedPolygon(null);
    }
    if (id === 'polygon' && setSelectedPolygon) {
      if (selectedValue === "all_polygons") {
        console.log("Selected All Polygons");
        // No need to restore allPolygons manually; clearing selectedPolygon will let LocationsProvider
        // re-calculate displayedPolygons (showing the full filtered list).
        setSelectedPolygon(null);
      } else {
        const selectedPolygonObject = options.find((option: any) => option.value === selectedValue);
        console.log("Full Polygon Info:", selectedPolygonObject?.polygon);
        setSelectedPolygon(selectedPolygonObject ? selectedPolygonObject.polygon : null);
        
        // Reset filters when a specific polygon is selected
        if (setSelectedPolygonType) setSelectedPolygonType("all");
        if (setSelectedCouriers) setSelectedCouriers([]);
      }
    }
  }

  const handle_toggle_courier = (courierOption: any) => {
    if (!selectedCouriers || !setSelectedCouriers) return;

    // Reset selectedPolygon when changing couriers (User action)
    if (setSelectedPolygon) setSelectedPolygon(null);

    const isSelected = selectedCouriers.some((c: any) => c.id === courierOption.value);
    let newSelection = [];

    if (isSelected) {
      newSelection = selectedCouriers.filter((c: any) => c.id !== courierOption.value);
    } else {
      newSelection = [...selectedCouriers, courierOption.courier];
    }

    setSelectedCouriers(newSelection);
  };

  const apply_filter_value = () => {
    apply_filter({ ..._field, value })
  }

  return (
    <Collapse
      name={id}
      header={
        <div className="flex items-center gap-2">
          <h1>{label}</h1>
          {isLoading && pathname === "/coverages/map" && <Spinner size="sm" color="info" aria-label="Cargando..." />}
        </div>
      }
    >
      {isLoading && pathname === "/coverages/map" ? (
        <div className="flex items-center justify-center p-4">
          <Spinner size="md" color="info" aria-label="Cargando opciones..." />
          <span className="ml-3 text-sm text-gray-500">Cargando...</span>
        </div>
      ) : id === 'courier' && pathname === "/coverages/map" ? (
        <div className="w-full flex flex-col gap-2 max-h-60 overflow-y-auto p-2">
          {options.length ? (
            <>
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                <Checkbox
                  id="courier-select-all"
                  checked={selectedCouriers?.length === options.length && options.length > 0}
                  onChange={() => {
                    if (selectedCouriers?.length === options.length) {
                      setSelectedCouriers([]);
                    } else {
                      const allCouriers = options.map((o: any) => o.courier);
                      setSelectedCouriers(allCouriers);
                    }
                  }}
                />
                <Label htmlFor="courier-select-all" className="cursor-pointer text-sm font-bold text-gray-900 dark:text-gray-300">
                  Seleccionar Todos
                </Label>
              </div>
              {options.map((item: any, i: number) => {
                const isChecked = selectedCouriers?.some((c: any) => c.id === item.value);
                return (
                  <div key={i} className="flex items-center gap-2">
                    <Checkbox
                      id={`courier-${item.value}`}
                      checked={isChecked}
                      onChange={() => handle_toggle_courier(item)}
                    />
                    <Label htmlFor={`courier-${item.value}`} className="cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-300">
                      {item.label}
                    </Label>
                  </div>
                );
              })}
            </>
          ) : (
            <p className="text-sm text-gray-500 text-center italic p-2">Sin opciones (seleccione una comuna)</p>
          )}
        </div>
      ) : (
        <div className="w-full flex flex-row items-center justify-center gap-2">
          <div className="relative w-full h-14">
            <Select onChange={handle_change_value} value={value} className="w-full h-full">
              {options.length ? (
                <>
                  <option value="" disabled>
                    <p>Seleccione...</p>
                  </option>
                  {options.map((item: any, i: number) => (
                    <option key={i} value={item?.value}>
                      <p>{item?.label}</p>
                    </option>
                  ))}
                </>
              ) : (
                <option disabled>Sin opciones</option>
              )}
            </Select>
          </div>
          {pathname !== "/coverages/map" && (
            <Button label="Aplicar" disabled={Boolean(!value)} className="w-28 !p-0" onClick={apply_filter_value} />
          )}
        </div>
      )}
    </Collapse>
  )
}

export default FieldSelectService